import { toGradeRank } from "~/components/MeqTable/sortFns";
import type { MeqColumnDef, MeqRowData } from "~/components/MeqTable/types";
import type { CameraColumnValueTypeV2 } from "~/hooks/Camera/v2";
import { isNotEmpty } from "~/utils/Helper";
import type { LooseObject } from "~/utils/Types";
import type { UsdaGradeBandId } from "~/utils/usdaGradeBands";
import { USDA_GRADE_BANDS, toUsdaGradeBand } from "~/utils/usdaGradeBands";

import type {
  ImagesDcuLiteFilter,
  ImagesFilterAvailability,
  ImagesFilterState,
  ImagesModalRowKeys,
  ImagesMsaFilter,
  ImagesSortState,
} from "./types";

/** The row key a column's values live under. */
export const getColumnDataKey = (
  column: MeqColumnDef<MeqRowData>,
): string | undefined => column.accessorKey ?? column.id;

/** How MeqTable identifies a column — TanStack resolves `id ?? accessorKey`. */
export const getColumnId = (
  column: MeqColumnDef<MeqRowData>,
): string | undefined => column.id ?? column.accessorKey;

export const hasColumn = (
  columns: MeqColumnDef<MeqRowData>[],
  key: string,
): boolean => columns.some((column) => getColumnDataKey(column) === key);

export const getFilterAvailability = (
  columns: MeqColumnDef<MeqRowData>[],
  keys: ImagesModalRowKeys,
): ImagesFilterAvailability => ({
  hasAusMarbling: hasColumn(columns, keys.ausMarbling),
  hasBodyId: hasColumn(columns, keys.bodyId),
  hasDcuLite: hasColumn(columns, keys.dcuLite),
  /**
   * "EMA missed only" is a QA control over the DCU's capture, so it belongs to
   * the sites a DCU runs at. The measurement alone cannot say which those are:
   * every region's preset carries `EYE_MUSCLE_AREA`, the US one renaming only
   * its label, to "Rib Eye Area". The OVERRIDE can — a site gets an override
   * column exactly where an operator can correct the model, which is the DCU.
   * Requiring the pair narrows this from every Australian location to the ten
   * on the DCU column preset.
   */
  hasEma: hasColumn(columns, keys.ema) && hasColumn(columns, keys.emaOverride),
  hasFatColor: hasColumn(columns, keys.fatColor),
  hasGrader: hasColumn(columns, keys.grader),
  hasMeatColor: hasColumn(columns, keys.meatColor),
  hasMsa: hasColumn(columns, keys.msa),
  hasSex: hasColumn(columns, keys.sex),
  hasUsdaGrade: hasColumn(columns, keys.usdaMarbling),
});

/** Anything unrecognised widens to "all" rather than stranding the view on a
 *  state its control cannot represent. */
export const toMsaFilter = (value: string): ImagesMsaFilter =>
  value === "msa" || value === "nonMsa" ? value : "all";

export const toDcuLiteFilter = (value: string): ImagesDcuLiteFilter =>
  value === "dcuLite" || value === "standard" ? value : "all";

/** The flag arrives as a JSON boolean, but a CSV round-trip or a legacy row can
 *  put the same value through as text. */
const isDcuLiteRow = (value: unknown): boolean =>
  value === true || value === "true";

const isBlank = (value: unknown): boolean =>
  value === null || value === undefined || value === "";

const isNumberLike = (value: unknown): boolean =>
  !isBlank(value) && typeof value !== "boolean" && !Number.isNaN(Number(value));

/**
 * Sorted distinct non-empty values of one row key — a value filter's options.
 * A column whose values all read as numbers (fat colour 0–5, AUS marbling
 * 0–9) lists in numeric order, so "10" never precedes "2"; anything else lists
 * lexically.
 */
export const getFilterOptions = (
  rows: LooseObject[],
  key: string,
): string[] => {
  const values = [
    ...new Set(
      rows.filter((row) => isNotEmpty(row[key])).map((row) => String(row[key])),
    ),
  ];
  return values.every(isNumberLike)
    ? values.sort((a, b) => Number(a) - Number(b))
    : values.sort();
};

/** The USDA grade bands the rows' marbling scores fall into, highest first —
 *  the order the bands rank in, which is also how the control lists them. */
export const getUsdaGradeOptions = (
  rows: LooseObject[],
  key: string,
): UsdaGradeBandId[] => {
  const present = new Set(rows.map((row) => toUsdaGradeBand(row[key])));
  return USDA_GRADE_BANDS.map((band) => band.id).filter((id) =>
    present.has(id),
  );
};

const isBodyIdRangeActive = (filters: ImagesFilterState): boolean =>
  typeof filters.bodyIdFrom === "number" ||
  typeof filters.bodyIdTo === "number";

export const isBodyIdRangeInvalid = (filters: ImagesFilterState): boolean =>
  typeof filters.bodyIdFrom === "number" &&
  typeof filters.bodyIdTo === "number" &&
  filters.bodyIdFrom > filters.bodyIdTo;

/** How many filters are narrowing, for the popover trigger's badge. */
export const countActiveFilters = (filters: ImagesFilterState): number =>
  (filters.graders.length > 0 ? 1 : 0) +
  (filters.usdaGrades.length > 0 ? 1 : 0) +
  (filters.ausMarblings.length > 0 ? 1 : 0) +
  (filters.meatColors.length > 0 ? 1 : 0) +
  (filters.fatColors.length > 0 ? 1 : 0) +
  (filters.sexes.length > 0 ? 1 : 0) +
  (filters.isEmaMissedOnly ? 1 : 0) +
  (filters.msaGrading === "all" ? 0 : 1) +
  (filters.dcuLite === "all" ? 0 : 1) +
  (isBodyIdRangeActive(filters) ? 1 : 0);

/** The i18n key naming one USDA grade band. */
export const usdaGradeLabelKey = (id: UsdaGradeBandId): string =>
  `imagesModal.grade${id.charAt(0).toUpperCase()}${id.slice(1)}`;

/**
 * Names the single narrowing filter, so the header can say which one it is
 * rather than just that something is on. Undefined once several are active —
 * there is no one name for that — and when none are.
 *
 * A filter over chosen values names the values ("ana, bo"), not the control
 * they came from: "Grader 42/1138" leaves out the only part worth reading.
 */
export const getSoleActiveFilterLabel = (
  filters: ImagesFilterState,
  t: (key: string) => string,
): string | undefined => {
  if (countActiveFilters(filters) !== 1) return undefined;
  if (filters.graders.length > 0) return filters.graders.join(", ");
  if (filters.usdaGrades.length > 0) {
    return filters.usdaGrades.map((id) => t(usdaGradeLabelKey(id))).join(", ");
  }
  if (filters.ausMarblings.length > 0) {
    return `${t("imagesModal.filterAusMarbling")} ${filters.ausMarblings.join(", ")}`;
  }
  if (filters.meatColors.length > 0) {
    return `${t("imagesModal.filterMeatColor")} ${filters.meatColors.join(", ")}`;
  }
  if (filters.fatColors.length > 0) {
    return `${t("imagesModal.filterFatColor")} ${filters.fatColors.join(", ")}`;
  }
  if (filters.sexes.length > 0) return filters.sexes.join(", ");
  if (filters.isEmaMissedOnly) return t("imagesModal.filterEmaMissing");
  if (filters.msaGrading === "msa") return t("imagesModal.filterMsaOnly");
  if (filters.msaGrading === "nonMsa") return t("imagesModal.filterNonMsaOnly");
  if (filters.dcuLite === "dcuLite") return t("imagesModal.filterDcuLiteOnly");
  if (filters.dcuLite === "standard")
    return t("imagesModal.filterStandardOnly");
  return t("imagesModal.filterBodyId");
};

/** A chosen-values filter passes a row when nothing is chosen or the row's
 *  value, in its string form, is among the choices. */
const matchesChosen = (chosen: Set<string>, value: unknown): boolean =>
  chosen.size === 0 || chosen.has(String(value));

/**
 * Shared across both modes — the table applies its search on top of this
 * result, the grid does not. Every filter must pass (AND), so combining two
 * narrows to their intersection.
 */
export const applyImageFilters = (
  rows: LooseObject[],
  filters: ImagesFilterState,
  keys: ImagesModalRowKeys,
): LooseObject[] => {
  if (isBodyIdRangeInvalid(filters)) return [];

  const graders = new Set(filters.graders);
  const usdaGrades = new Set<string>(filters.usdaGrades);
  const ausMarblings = new Set(filters.ausMarblings);
  const meatColors = new Set(filters.meatColors);
  const fatColors = new Set(filters.fatColors);
  const sexes = new Set(filters.sexes);
  const isRangeActive = isBodyIdRangeActive(filters);

  return rows.filter((row) => {
    if (!matchesChosen(graders, row[keys.grader])) return false;
    if (
      usdaGrades.size > 0 &&
      !usdaGrades.has(toUsdaGradeBand(row[keys.usdaMarbling]) ?? "")
    ) {
      return false;
    }
    if (!matchesChosen(ausMarblings, row[keys.ausMarbling])) return false;
    if (!matchesChosen(meatColors, row[keys.meatColor])) return false;
    if (!matchesChosen(fatColors, row[keys.fatColor])) return false;
    if (!matchesChosen(sexes, row[keys.sex])) return false;
    if (filters.isEmaMissedOnly && isNotEmpty(row[keys.ema])) return false;
    if (
      filters.msaGrading !== "all" &&
      isNotEmpty(row[keys.msa]) !== (filters.msaGrading === "msa")
    ) {
      return false;
    }
    if (
      filters.dcuLite !== "all" &&
      isDcuLiteRow(row[keys.dcuLite]) !== (filters.dcuLite === "dcuLite")
    ) {
      return false;
    }
    if (!isRangeActive) return true;

    const value = row[keys.bodyId];
    if (isBlank(value)) return false;
    const bodyId = typeof value === "number" ? value : Number(value);
    if (Number.isNaN(bodyId)) return false;
    if (typeof filters.bodyIdFrom === "number" && bodyId < filters.bodyIdFrom) {
      return false;
    }
    if (typeof filters.bodyIdTo === "number" && bodyId > filters.bodyIdTo) {
      return false;
    }
    return true;
  });
};

/**
 * Kill date carries no time at any level of the camera feed — the plant sends
 * the kill day, and every store below it types the column as a date. Body
 * number is that day's chain sequence and arrives on the same DCU feed, so it
 * is populated exactly where kill date is: the tie-break can never fire on rows
 * that lack it.
 */
const getTieBreakKey = (
  columnKey: string,
  keys: ImagesModalRowKeys,
): string | undefined =>
  columnKey === keys.killDate ? keys.bodyId : undefined;

const compareNumbers = (a: number, b: number): number =>
  a === b ? 0 : a < b ? -1 : 1;

const compareStrings = (a: string, b: string): number =>
  a === b ? 0 : a < b ? -1 : 1;

const getValueType = (
  columns: MeqColumnDef<MeqRowData>[],
  columnKey: string,
): CameraColumnValueTypeV2 | undefined => {
  const column = columns.find((entry) => getColumnDataKey(entry) === columnKey);
  const { valueType } = (column?.meta ?? {}) as {
    valueType?: CameraColumnValueTypeV2;
  };
  return valueType;
};

/**
 * Compares two present values of one column.
 *
 * A grade's own ordering is neither numeric nor lexical ("10+" outranks "10",
 * "2" ranks below both), so a `grade` column resolves through its rank.
 * Otherwise numbers order numerically — a `number` column's values can arrive
 * as strings, an `ext` column being whatever type its ClickHouse view
 * declares, and the v1 feeds carry no type metadata at all — and anything
 * that does not read as a number orders lexically after every number, so a
 * column mixing the two still has one consistent order. The feed's dates are
 * ISO strings, which order correctly as text.
 */
const compareValues = (
  a: unknown,
  b: unknown,
  valueType: CameraColumnValueTypeV2 | undefined,
): number => {
  if (valueType === "grade") {
    return compareNumbers(toGradeRank(a), toGradeRank(b));
  }
  const aIsNumber = isNumberLike(a);
  const bIsNumber = isNumberLike(b);
  if (aIsNumber && bIsNumber) return compareNumbers(Number(a), Number(b));
  if (aIsNumber !== bIsNumber) return aIsNumber ? -1 : 1;
  return compareStrings(String(a), String(b));
};

/** Body number arrives as a string, so it has to sort as a number — otherwise
 *  "10" precedes "2". */
const toSortableNumber = (value: unknown): number => {
  if (isBlank(value)) return Number.NEGATIVE_INFINITY;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? Number.NEGATIVE_INFINITY : parsed;
};

/**
 * THE comparator behind both modes' sort of one column, over two rows that
 * both carry a value for it. Ascending; the caller flips for descending. The
 * grid and the table each resolve their order through this one function, so
 * the two can never disagree on what "sorted by AUS marbling" means.
 */
export const compareImageRows = (
  columns: MeqColumnDef<MeqRowData>[],
  columnKey: string,
  keys: ImagesModalRowKeys,
): ((a: LooseObject, b: LooseObject) => number) => {
  const valueType = getValueType(columns, columnKey);
  const tieBreakKey = getTieBreakKey(columnKey, keys);

  return (a, b) => {
    const primary = compareValues(a[columnKey], b[columnKey], valueType);
    if (primary !== 0 || tieBreakKey === undefined) return primary;
    return compareNumbers(
      toSortableNumber(a[tieBreakKey]),
      toSortableNumber(b[tieBreakKey]),
    );
  };
};

/**
 * Grid-mode sort. Rows with no value sink to the bottom in either direction —
 * a blank is never the "largest" or "smallest" grade, it is absent.
 */
export const sortImageRows = (
  rows: LooseObject[],
  sort: ImagesSortState | undefined,
  keys: ImagesModalRowKeys,
  columns: MeqColumnDef<MeqRowData>[],
): LooseObject[] => {
  if (!sort) return rows;

  const graded = rows.filter((row) => !isBlank(row[sort.columnKey]));
  const ungraded = rows.filter((row) => isBlank(row[sort.columnKey]));

  const compare = compareImageRows(columns, sort.columnKey, keys);
  const direction = sort.isAscending ? 1 : -1;

  return graded.sort((a, b) => direction * compare(a, b)).concat(ungraded);
};

/**
 * Table-mode counterpart of `sortImageRows`: every column takes a comparator
 * built on the same `compareImageRows`, so a header click orders the table
 * exactly as the grid orders its tiles. The engine flips the comparator's sign
 * for a descending header, so a blank has to be reported as "smallest" under
 * descending and "largest" under ascending to land at the bottom either way —
 * which is why the active sort's direction is an input. `sortUndefined` is
 * switched off so the engine never places a missing key before this does.
 */
export const withTableSorting = (
  columns: MeqColumnDef<MeqRowData>[],
  keys: ImagesModalRowKeys,
  sort: ImagesSortState | undefined,
): MeqColumnDef<MeqRowData>[] =>
  columns.map((column) => {
    const dataKey = getColumnDataKey(column);
    if (dataKey === undefined) return column;

    const compare = compareImageRows(columns, dataKey, keys);
    const blankSign =
      sort !== undefined && sort.columnKey === dataKey && !sort.isAscending
        ? -1
        : 1;

    return {
      ...column,
      sortUndefined: false,
      sortingFn: (rowA, rowB) => {
        const a = rowA.original[dataKey];
        const b = rowB.original[dataKey];
        const aBlank = isBlank(a);
        const bBlank = isBlank(b);
        if (aBlank && bBlank) return 0;
        if (aBlank) return blankSign;
        if (bBlank) return -blankSign;
        return compare(rowA.original, rowB.original);
      },
    };
  });
